import { useEffect, useRef } from 'react';
import { X, KeyRound, ChevronDown } from 'lucide-react';
import Button from '../../../components/Button/Button';
import { useDrawerDrag } from '../../../hooks/useDrawerDrag';
import styles from './Team.module.css';

function generatePassword() {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

function MemberModal({
  mode,
  form,
  errors,
  loading,
  onClose,
  onSetField,
  onToggleAssetClass,
  onToggleAccess,
  onSubmit,
}) {
  const isEdit   = mode === 'edit';
  const errorRef = useRef(null);
  const { modalRef, dragHandleProps } = useDrawerDrag(onClose);

  useEffect(() => {
    if (errors.general && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [errors.general]);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} ref={modalRef} onClick={e => e.stopPropagation()}>
        <div className={styles.pullHandle} {...dragHandleProps} />
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{isEdit ? 'Edit member' : 'Add member'}</h2>
          <button className={styles.closeBtn} onClick={onClose}><X size={20} /></button>
        </div>

        <div className={styles.modalBody}>
          <p className={styles.sectionLabel}>LOGIN INFORMATION</p>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.fieldLabel}>First name</label>
              <input
                className={`${styles.fieldInput} ${errors.firstName ? styles.fieldInputError : ''}`}
                value={form.firstName}
                onChange={e => onSetField('firstName', e.target.value)}
              />
              {errors.firstName && <p className={styles.fieldError}>{errors.firstName}</p>}
            </div>
            <div className={styles.formGroup}>
              <label className={styles.fieldLabel}>Last name</label>
              <input
                className={`${styles.fieldInput} ${errors.lastName ? styles.fieldInputError : ''}`}
                value={form.lastName}
                onChange={e => onSetField('lastName', e.target.value)}
              />
              {errors.lastName && <p className={styles.fieldError}>{errors.lastName}</p>}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.fieldLabel}>Email</label>
            <input
              type="email"
              className={`${styles.fieldInput} ${errors.email ? styles.fieldInputError : ''}`}
              value={form.email}
              onChange={e => onSetField('email', e.target.value)}
            />
            {errors.email && <p className={styles.fieldError}>{errors.email}</p>}
          </div>

          {!isEdit && (
            <div className={styles.formGroup}>
              <label className={styles.fieldLabel}>Password</label>
              <div className={styles.passwordWrap}>
                <input
                  type={form.showPassword ? 'text' : 'password'}
                  placeholder="Generate password"
                  className={`${styles.fieldInput} ${styles.fieldInputPadRight} ${errors.password ? styles.fieldInputError : ''}`}
                  value={form.password}
                  onChange={e => onSetField('password', e.target.value)}
                />
                <button
                  type="button"
                  className={styles.passwordKeyBtn}
                  title="Generate password"
                  onClick={() => onSetField('password', generatePassword())}
                >
                  <KeyRound size={16} />
                </button>
              </div>
              {errors.password && <p className={styles.fieldError}>{errors.password}</p>}
              <label className={styles.showPwdLabel}>
                <input
                  type="checkbox"
                  className={styles.squareCheck}
                  checked={form.showPassword}
                  onChange={e => onSetField('showPassword', e.target.checked)}
                />
                Show password
              </label>
            </div>
          )}

          {/* REQUEST NEW ACCOUNT — Account type (hidden for now)
          {!isEdit && (
            <>
              <p className={styles.sectionLabel}>REQUEST NEW ACCOUNT</p>
              <div className={styles.formGroup}>
                <label className={styles.fieldLabel}>Account type</label>
                <div className={styles.selectWrap}>
                  <select
                    className={styles.fieldInput}
                    value={form.accountType}
                    onChange={e => onSetField('accountType', e.target.value)}
                  >
                    <option>Instant Funding DFY</option>
                    <option>Standard</option>
                    <option>Pro</option>
                  </select>
                  <ChevronDown size={14} className={styles.selectChevron} />
                </div>
              </div>
            </>
          )}
          */}

          <p className={styles.sectionLabel}>ACCESS</p>
          <div className={styles.formGroup}>
            <div className={styles.radioGroup}>
              {['Standard', 'Admin'].map(opt => (
                <label key={opt} className={styles.radioItem}>
                  <input
                    type="radio"
                    name="access"
                    checked={form.access === opt.toLowerCase()}
                    onChange={() => onToggleAccess(opt.toLowerCase())}
                  />
                  {opt}
                </label>
              ))}
            </div>
            {errors.access && <p className={styles.fieldError}>{errors.access}</p>}
          </div>
        </div>

        {errors.general && (
          <p ref={errorRef} className={styles.generalError}>{errors.general}</p>
        )}

        {/* Desktop keeps Cancel-then-confirm reading order; mobile flips the
            column so the primary action sits on top (see .memberFooter). */}
        <div className={`${styles.modalFooter} ${styles.memberFooter}`}>
          <Button
            variant="secondary"
            size="sm"
            fullWidth={false}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            fullWidth={false}
            onClick={onSubmit}
            disabled={loading}
          >
            {loading ? (isEdit ? 'Saving…' : 'Adding…') : (isEdit ? 'Save changes' : 'Add member')}
          </Button>
        </div>

      </div>
    </div>
  );
}

export default MemberModal;
